// ─────────────────────────────────────────────────────────────────────────
// Editorial + utility page content for Ecoffee Cup® South Africa.
// Nav IA, About, Sustainability, Care, FAQs, Gifting, Stockists, Contact.
// ─────────────────────────────────────────────────────────────────────────

// ── Navigation IA ────────────────────────────────────────────────────────
/** Shop dropdown — deliberately flat and simple. */
export const shopMenu = [
  { label: 'Choose Your Story', to: '/stories' },
  { label: 'Shop All', to: '/shop' },
  { label: 'Shop by Collection', to: '/collections' },
  { label: 'Shop by Size', to: '/shop#size' },
  { label: 'Gifting', to: '/gifting' },
  { label: 'Corporate Branding', to: '/corporate-branding' },
  { label: 'Size & Drink Guide', to: '/size-guide' },
]

/** Help dropdown — every page stands on its own, no sub-navigation. */
export const helpMenu = [
  { label: 'Care', to: '/care' },
  { label: 'FAQs', to: '/faqs' },
  { label: 'Shipping & Returns', to: '/shipping-returns' },
  { label: 'Contact', to: '/contact' },
]

/** About dropdown — same flat pattern as Shop, anchored into the one page. */
export const aboutMenu = [
  { label: 'Our Story', to: '/about#our-story' },
  { label: 'Sustainability', to: '/about#sustainability' },
  {
    label: 'South African Ecoffee Cup®',
    to: '/about#sa-story',
  },
]

/** In-page anchors on the About page itself. */
export const aboutAnchors = [
  { label: 'Our Story', to: '#our-story' },
  { label: 'Sustainability', to: '#sustainability' },
  { label: 'South African Ecoffee Cup®', to: '#sa-story' },
]

// ── About / Our Story ────────────────────────────────────────────────────
export const aboutIntro =
  'Ecoffee Cup® started with a simple, stubborn idea: the cup you use every day should be worth using every day. In South Africa that means designing for a stoep in Prince Albert and a queue on Bree Street with equal seriousness.'

export const aboutChapters = [
  {
    id: 'the-e',
    eyebrow: 'The E stands for everyone',
    title: 'Not a lecture. A better cup.',
    body: 'The E in Ecoffee Cup® has never stood for eco. It stands for everyone — because a reusable cup only works if it is the one you actually want to pick up. So we start with the design, the weight in the hand, the way the lid feels, and let the good habit follow.',
    image:
      'https://images.unsplash.com/photo-1511537190424-bbbab87ac5eb?auto=format&fit=crop&q=80&w=1400',
  },
  {
    id: 'south-africa',
    eyebrow: 'Made for here',
    title: 'A local story layer, a global design house.',
    body: 'We bring the official Collections — William Morris, the Van Gogh Museum, Emma Shipley, Kew Gardens — to South African shelves. Alongside them we build Choose Your Story: ranges drawn from the Karoo, the Cape Peninsula and the fynbos, made with artists who live here.',
    image:
      'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&q=80&w=1400',
  },
  {
    id: 'craft',
    eyebrow: 'How it is made',
    title: 'Bamboo fibre, food-grade silicone, no fuss.',
    body: 'Every cup is built from natural bamboo fibre with a food-grade silicone lid and sleeve. Dishwasher safe, BPA free, and designed to be replaced part by part rather than thrown away whole.',
    image:
      'https://images.unsplash.com/photo-1442512595331-e89e73853f31?auto=format&fit=crop&q=80&w=1400',
  },
]

export const aboutFacts = [
  { value: '2014', label: 'First cup made' },
  { value: '3', label: 'Sizes, metric' },
  { value: '40+', label: 'Stockists nationwide' },
  { value: '4', label: 'Official collections' },
]

// ── Sustainability ───────────────────────────────────────────────────────
export const sustainabilityIntro =
  'We are not going to tell you that one cup saves the planet. We will tell you exactly what this one is made of, how long it lasts, and what happens at the end.'

export const sustainabilityPillars = [
  {
    id: 'materials',
    eyebrow: 'Materials',
    title: 'Bamboo fibre, not new plastic',
    body: 'The body is made from natural bamboo fibre — a fast-growing grass that needs no replanting. Lid and sleeve are food-grade silicone: soft, durable, and BPA free.',
  },
  {
    id: 'longevity',
    eyebrow: 'Longevity',
    title: 'Designed to be repaired, not replaced',
    body: 'Lids and sleeves are sold separately. Lose a lid and you replace a lid — the cup itself is built for years of daily use and the dishwasher.',
  },
  {
    id: 'end-of-life',
    eyebrow: 'End of life',
    title: 'Separable, honestly',
    body: 'When a cup is finally finished, the silicone and the bamboo body separate by hand so each part can go where it should. No glue, no mystery composite.',
  },
]

// ── The South African story (About → #sa-story) ──────────────────────────
export const saStoryIntro =
  'Alongside the official Collections we build our own ranges here, with artists who live here. We call it Choose Your Story — a place or a mood from this country, told properly, with the cups to match.'

export const saStoryPoints = [
  {
    title: 'Made with local artists',
    body: 'Colourists and botanical painters from Prince Albert to Stellenbosch, paid properly and credited by name.',
  },
  {
    title: 'Places, not slogans',
    body: 'The Karoo at six in the morning. Two oceans arguing at Cape Point. Fynbos after rain. One numbered winter release.',
  },
  {
    title: 'A separate path to the Collections',
    body: 'Choose Your Story sits beside William Morris and the Van Gogh Museum — never folded into them.',
  },
]

export const sustainabilityStats = [
  { value: '1', label: 'Cup, used daily, replaces hundreds of single-use' },
  { value: '0', label: 'BPA, phthalates or melamine' },
  { value: '100%', label: 'Separable parts at end of life' },
]

// ── Care ─────────────────────────────────────────────────────────────────
export const careSteps = [
  {
    step: '01',
    title: 'Rinse before the first use',
    body: 'A warm rinse with a drop of dish soap is all it needs. No need to sterilise.',
  },
  {
    step: '02',
    title: 'Dishwasher, top shelf',
    body: 'Cup, lid and sleeve are all dishwasher safe. Keep them on the top shelf, away from the heating element.',
  },
  {
    step: '03',
    title: 'Let the lid dry open',
    body: 'Pop the silicone lid off and let both parts air-dry separately. This is the single best thing you can do for the life of the cup.',
  },
  {
    step: '04',
    title: 'Skip the microwave',
    body: 'Bamboo fibre and microwaves do not get along. Fill from a kettle or a barista, never from a microwave.',
  },
]

export const careDos = [
  'Dishwasher safe, top shelf',
  'Hot drinks up to 100°C',
  'Hand wash with any mild soap',
  'Replace lids and sleeves individually',
]

export const careDonts = [
  'No microwave',
  'No freezer',
  'No bleach or abrasive scourers',
  'Do not leave liquid standing overnight',
]

// ── FAQs ─────────────────────────────────────────────────────────────────
export const faqGroups = [
  {
    id: 'product',
    title: 'The cup',
    items: [
      {
        q: 'What is an Ecoffee Cup® made of?',
        a: 'A natural bamboo fibre body with a food-grade silicone lid and sleeve. BPA free, no melamine, no phthalates.',
      },
      {
        q: 'Which size should I choose?',
        a: 'The 250ml suits espresso and cortado, the 350ml is our all-rounder for flat whites and cappuccinos, and the 400ml is for americanos, filter and long mornings. The Size & Drink Guide sets all three out side by side.',
      },
      {
        q: 'Is it leak-proof?',
        a: 'The silicone lid is leak-resistant and designed for one-handed drinking on the move. We would not recommend packing it upside down in a laptop bag.',
      },
      {
        q: 'Will a café fill my own cup?',
        a: 'Almost every independent café in South Africa will, and many offer a small discount for it. Ask at the till.',
      },
    ],
  },
  {
    id: 'orders',
    title: 'Orders, shipping & returns',
    items: [
      {
        q: 'How long does delivery take?',
        a: 'Two to four working days to the major metros and three to six days to outlying areas. You will get a tracking link as soon as your parcel is collected.',
      },
      {
        q: 'What does shipping cost?',
        a: 'Flat-rate nationwide courier, free on orders over R 750.',
      },
      {
        q: 'Can I return or exchange a cup?',
        a: 'Unused cups in their original, unopened packaging can be returned within 7 days. Faulty products are covered separately under the Consumer Protection Act. Full detail lives on our Shipping & Returns page.',
      },
      {
        q: 'Do you ship outside South Africa?',
        a: 'Not from this store. For orders elsewhere in Africa, get in touch and we will point you to the closest distributor.',
      },
    ],
  },
  {
    id: 'gifting',
    title: 'Gifting & corporate',
    items: [
      {
        q: 'Can I add a gift note?',
        a: 'Yes. Add your message at checkout and we will include it on an unbranded card with no pricing on the packing slip.',
      },
      {
        q: 'Do you brand cups for companies?',
        a: 'We do — custom sleeves, printing on solid colour cups, or a fully bespoke cup, from 50 units with artwork sign-off before production. Everything is set out on our Corporate Branding page.',
      },
    ],
  },
]

// ── Gifting ──────────────────────────────────────────────────────────────
export const corporateFacts = [
  {
    label: 'Minimum order',
    value: '50 units',
    detail: 'Per design, per size. Mixed colourways welcome above 150 units.',
  },
  {
    label: 'Branding methods',
    value: 'Three routes',
    detail: 'Custom sleeves, printing on solid colour cups, or fully bespoke customisation.',
  },
  {
    label: 'Lead time',
    value: '3–4 weeks',
    detail: 'From artwork sign-off to delivery, nationwide.',
  },
  {
    label: 'Artwork',
    value: 'Vector, sent by us',
    detail: 'We send a dieline and a digital proof for approval before any production run.',
  },
  {
    label: 'Colour matching',
    value: 'Pantone on request',
    detail: 'Sleeve and lid can be matched to your brand palette above 250 units.',
  },
  {
    label: 'Packaging',
    value: 'Plain or branded',
    detail: 'Individual gift boxes, or bulk-packed for internal distribution.',
  },
  {
    label: 'Nationwide delivery',
    value: 'Included',
    detail: 'Single or split delivery to multiple offices at no extra cost.',
  },
  {
    label: 'Samples',
    value: 'Available',
    detail: 'Request a physical sample before committing to a run.',
  },
]

/**
 * The three branding routes, shown as a 3-column layout on Gifting.
 * `images` feed a simple carousel — add or remove images per column.
 */
export const brandingOptions = [
  {
    id: 'sleeves',
    title: 'Custom sleeves',
    body: 'Your artwork printed full-colour on the silicone sleeve. The cup stays one of our standard designs, which keeps cost and lead time down.',
    images: [
      'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=1000',
      'https://images.unsplash.com/photo-1509042239860-f550ce710b93?auto=format&fit=crop&q=80&w=1000',
    ],
    facts: [
      { label: 'From', value: 'R___ per cup' },
      { label: 'Minimum order', value: '50 units' },
      { label: 'Lead time', value: '3 weeks' },
    ],
  },
  {
    id: 'solid-print',
    title: 'Printing on solid colour cups',
    body: 'A logo or short line printed directly onto one of our solid colourway cups. The most popular route for staff gifting.',
    images: [
      'https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&q=80&w=1000',
      'https://images.unsplash.com/photo-1511537190424-bbbab87ac5eb?auto=format&fit=crop&q=80&w=1000',
    ],
    facts: [
      { label: 'From', value: 'R___ per cup' },
      { label: 'Minimum order', value: '100 units' },
      { label: 'Lead time', value: '3–4 weeks' },
    ],
  },
  {
    id: 'bespoke',
    title: 'Bespoke customisation',
    body: 'A cup built around your brand — full-wrap artwork on the body, with matched lid and sleeve colours on request.',
    images: [
      'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1000',
      'https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=80&w=1000',
    ],
    facts: [
      { label: 'From', value: 'R___ per cup' },
      { label: 'Minimum order', value: '250 units' },
      { label: 'Lead time', value: '5–6 weeks' },
    ],
  },
]

export const corporateExamples = [
  {
    name: 'Truth Coffee, Cape Town',
    detail: '400 branded 350ml cups for their loyalty programme.',
    image:
      'https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&q=80&w=1000',
  },
  {
    name: 'Highveld Legal, Sandton',
    detail: '180 laser-marked cups replacing the boardroom paper cup.',
    image:
      'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=1000',
  },
  {
    name: 'Stellenbosch Wine Route',
    detail: 'Co-branded 250ml cups for a summer festival run.',
    image:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1000',
  },
]

export const giftingOccasions = [
  {
    id: 'weddings',
    title: 'Weddings & celebrations',
    body: 'Table gifts guests will actually take home. Colour-matched to your palette above 250 units.',
  },
  {
    id: 'conferences',
    title: 'Conferences & events',
    body: 'One cup per delegate instead of a thousand paper ones. Branded sleeves, bulk-packed.',
  },
  {
    id: 'onboarding',
    title: 'Staff onboarding',
    body: 'A welcome gift for new starters that survives the whole first year.',
  },
  {
    id: 'seasonal',
    title: 'Year-end & festive',
    body: 'Our busiest season — book artwork sign-off by mid-October.',
  },
]

// ── Stockists ────────────────────────────────────────────────────────────
export type Stockist = {
  name: string
  city: string
  address: string
  lat: number
  lng: number
}

export const stockistRegions: {
  id: string
  region: string
  stores: Stockist[]
}[] = [
  {
    id: 'western-cape',
    region: 'Western Cape',
    stores: [
      {
        name: 'Truth Coffee Roasting',
        city: 'Cape Town CBD',
        address: '36 Buitenkant St, Cape Town',
        lat: -33.9276,
        lng: 18.4241,
      },
      {
        name: 'Rosetta Roastery',
        city: 'Woodstock',
        address: '414 Albert Rd, Woodstock',
        lat: -33.9282,
        lng: 18.4482,
      },
      {
        name: 'Schoon',
        city: 'Stellenbosch',
        address: 'Oude Molen, Vlottenburg',
        lat: -33.9464,
        lng: 18.8046,
      },
      {
        name: 'The Ranger Station',
        city: 'Salt River',
        address: '129 Lower Main Rd, Salt River',
        lat: -33.9302,
        lng: 18.4569,
      },
    ],
  },
  {
    id: 'gauteng',
    region: 'Gauteng',
    stores: [
      {
        name: 'Father Coffee',
        city: 'Rosebank',
        address: 'Keyes Art Mile, Rosebank',
        lat: -26.1421,
        lng: 28.0421,
      },
      {
        name: 'Doubleshot',
        city: 'Braamfontein',
        address: '15 Juta St, Braamfontein',
        lat: -26.1929,
        lng: 28.0341,
      },
      {
        name: 'Bean There',
        city: 'Milpark',
        address: '44 Stanley Ave, Milpark',
        lat: -26.1795,
        lng: 28.0122,
      },
      {
        name: 'Coco Safar',
        city: 'Sandton',
        address: 'Sandton City, Sandton',
        lat: -26.1076,
        lng: 28.0567,
      },
    ],
  },
  {
    id: 'kzn',
    region: 'KwaZulu-Natal',
    stores: [
      {
        name: 'Colombo Coffee',
        city: 'Durban',
        address: '48 Old Main Rd, Hillcrest',
        lat: -29.7796,
        lng: 30.7654,
      },
      {
        name: 'Terbodore',
        city: 'Midlands',
        address: 'Currys Post Rd, Howick',
        lat: -29.4626,
        lng: 30.1946,
      },
    ],
  },
  {
    id: 'eastern-cape',
    region: 'Eastern Cape',
    stores: [
      {
        name: 'Masala Dosa',
        city: 'Gqeberha',
        address: '25 Stanley St, Richmond Hill',
        lat: -33.9575,
        lng: 25.5983,
      },
      {
        name: 'Bean Barn',
        city: 'East London',
        address: 'Beacon Bay, East London',
        lat: -32.9783,
        lng: 27.9116,
      },
    ],
  },
]

export const stockistBenefits = [
  {
    title: 'Trade pricing',
    body: 'Tiered wholesale pricing from a 24-unit opening order.',
  },
  {
    title: 'Merchandising support',
    body: 'Counter displays, shelf talkers and product photography, free.',
  },
  {
    title: 'Listed here',
    body: 'Every active stockist appears on our Find a Stockist page.',
  },
  {
    title: 'No exclusivity lock-in',
    body: 'Month-to-month. Reorder when you need to, at your own pace.',
  },
]

// ── Contact ──────────────────────────────────────────────────────────────
export const contactDetails = [
  { label: 'Customer care', value: 'hello@ecoffeecup.co.za' },
  { label: 'Corporate & branding', value: 'corporate@ecoffeecup.co.za' },
  { label: 'Wholesale', value: 'trade@ecoffeecup.co.za' },
  { label: 'Telephone', value: '+27 21 000 0000' },
]

export const contactHours = [
  { day: 'Monday – Thursday', hours: '08h30 – 17h00' },
  { day: 'Friday', hours: '08h30 – 15h30' },
  { day: 'Weekends & public holidays', hours: 'Closed' },
]

export const contactTopics = [
  'An order',
  'A product question',
  'Corporate gifting',
  'Becoming a stockist',
  'Something else',
]
